import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/CollectionAndDelivery.css';
import { FaCloudUploadAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { auth, db, storage } from '../firebaseConfig';
import {
  collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

/* ───────── helpers ───────── */
const fileKind = (name, type) => {
  if (/pdf$/i.test(name) || type === 'application/pdf') return 'pdf';
  if (/csv$/i.test(name) || type === 'text/csv') return 'csv';
  if (/image\//.test(type)) return 'image';
  return 'other';
};
const MAX_FILES = 10;
const MAX_SIZE_MB = 10;
const TRIP_TYPES = ['Delivery', 'Collection'];
const TRIP_STATUSES = ['Scheduled', 'Done'];
const OVERNIGHT_EXTRA = 650;

export default function CollectionAndDelivery() {
  /* ───────── routing ───────── */
  const navigate = useNavigate();
  const location = useLocation();
  const queryJobId = new URLSearchParams(location.search).get('jobId'); // ?jobId=NYA…

  /* ───────── basic state ───────── */
  const [tripId, setTripId] = useState('Loading…');
  const [tripType, setTripType] = useState(TRIP_TYPES[0]);
  const [tripDate, setTripDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [tripStatus, setTripStatus] = useState(TRIP_STATUSES[0]);

  /* driver (select) */
  const [drivers, setDrivers] = useState([]);
  const [driverSelId, setDriverSelId] = useState('');
  const driverSel = useMemo(() => drivers.find(d => d.id === driverSelId) || null, [drivers, driverSelId]);

  /* vehicle (select) */
  const [vehicles, setVehicles] = useState([]);
  const [vehicleSelId, setVehicleSelId] = useState('');
  const vehicleSel = useMemo(() => vehicles.find(v => v.id === vehicleSelId) || null, [vehicles, vehicleSelId]);

  /* jobs & rfqs */
  const [jobs, setJobs] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [assignedId, setAssignedId] = useState('');

  /* trip costs templates */
  const [tripCosts, setTripCosts] = useState([]);
  const [tripCostTemplateId, setTripCostTemplateId] = useState('');
  const [baseCost, setBaseCost] = useState(''); // editable base (from template or manual)

  /* overnight */
  const [sleepover, setSleepover] = useState(false);

  /* computed totals */
  const totalCost = useMemo(() => {
    const base = parseFloat(baseCost) || 0;
    return base + (sleepover ? OVERNIGHT_EXTRA : 0);
  }, [baseCost, sleepover]);

  /* files */
  const [files, setFiles] = useState([]);
  const [fileMeta, setFileMeta] = useState([]);
  const [dragActive, setDrag] = useState(false);

  /* ui flags */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* validation */
  const [errors, setErrors] = useState({
    tripId: false,
    driverSelId: false,
    vehicleSelId: false,
    assignedId: false
  });

  const fileInputRef = useRef(null);

  /* ───────── initial fetch ───────── */
  useEffect(() => {
    const init = async user => {
      if (!user) { toast.error('Please login to create a trip.'); setLoading(false); return; }
      try {
        setLoading(true);

        // next Trip number (scoped to user)
        const tripSnap = await getDocs(query(collection(db, 'Trips'), where('authId', '==', user.uid)));
        setTripId(`TRIP${tripSnap.size + 1}`);

        // parallel loads
        const [jobSnap, rfqSnap, drvSnap, vehSnap, costSnap] = await Promise.all([
          getDocs(collection(db, 'Jobs')),
          getDocs(collection(db, 'RFQs')),
          getDocs(collection(db, 'Drivers')),
          getDocs(collection(db, 'Vehicles')),
          getDocs(collection(db, 'TripCosts'))
        ]);

        const jobsArr = jobSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const rfqArr  = rfqSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const drvArr  = drvSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const vehArr  = vehSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const costArr = costSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        setJobs(jobsArr);
        setRfqs(rfqArr);
        setDrivers(drvArr);
        setVehicles(vehArr);
        setTripCosts(costArr);

        // auto‑select job if ?jobId=… supplied
        if (queryJobId) {
          const found = jobsArr.find(j => j.jobId === queryJobId);
          if (found) { setAssignedId(found.id); setTripType('Delivery'); }
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch prerequisite data.');
      } finally { setLoading(false); }
    };
    const unsub = auth.onAuthStateChanged(init);
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ───────── validation ───────── */
  const validate = () => {
    const v = {
      tripId: !tripId.trim(),
      driverSelId: !driverSelId,
      vehicleSelId: !vehicleSelId,
      assignedId: !assignedId
    };
    setErrors(v);
    return !Object.values(v).some(Boolean);
  };

  /* ───────── upload helpers ───────── */
  const addFiles = newFiles => {
    if (!newFiles.length) return;
    const exist = [...files], meta = [...fileMeta];

    for (const f of newFiles) {
      if (exist.length >= MAX_FILES) { toast.error(`Max ${MAX_FILES} files reached.`); break; }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) { toast.error(`${f.name} exceeds ${MAX_SIZE_MB} MB.`); continue; }
      const kind = fileKind(f.name, f.type);
      exist.push(f);
      meta.push({ name: f.name, kind, preview: kind === 'image' ? URL.createObjectURL(f) : null });
    }
    setFiles(exist); setFileMeta(meta);
  };
  const handleInputFiles = e => addFiles(Array.from(e.target.files || []));
  const onDragOver = e => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);
  const onDrop = e => { e.preventDefault(); setDrag(false); addFiles(Array.from(e.dataTransfer.files || [])); };
  const openPicker = () => fileInputRef.current?.click();
  const removeFile = idx => {
    setFiles(f => f.filter((_, i) => i !== idx));
    setFileMeta(m => {
      const t = m[idx]; if (t?.preview) URL.revokeObjectURL(t.preview);
      return m.filter((_, i) => i !== idx);
    });
  };
  useEffect(() => () => fileMeta.forEach(m => m.preview && URL.revokeObjectURL(m.preview)), [fileMeta]);

  const uploadAll = async idStr => {
    if (!files.length) return { fileMeta: [], fileUrls: [] };
    const uploaded = await Promise.all(
      files.map(async f => {
        const sRef = ref(storage, `Trips/${idStr}/${Date.now()}_${f.name}`);
        await uploadBytes(sRef, f);
        const url = await getDownloadURL(sRef);
        return { name: f.name, url, kind: fileKind(f.name, f.type) };
      })
    );
    return { fileMeta: uploaded, fileUrls: uploaded.map(u => u.url) };
  };

  /* ───────── interactions ───────── */
  // when a template is picked, load the base amount
  const onPickTemplate = e => {
    const id = e.target.value;
    setTripCostTemplateId(id);
    const sel = tripCosts.find(c => c.id === id);
    setBaseCost(sel ? Number(sel.amount || 0) : '');
  };

  const onToggleSleepover = checked => {
    setSleepover(checked);
    if (checked) toast.info(`R${OVERNIGHT_EXTRA.toFixed(0)} overnight extra applied to total.`);
  };

  /* ───────── save ───────── */
  const saveTrip = async e => {
    e.preventDefault();
    if (!validate()) { toast.error('Fix highlighted fields.'); return; }
    const user = auth.currentUser;
    if (!user) { toast.error('Please login first.'); return; }
    try {
      setSaving(true);
      // duplicate guard
      const dupSnap = await getDocs(query(collection(db, 'Trips'), where('tripId', '==', tripId.trim())));
      if (!dupSnap.empty) {
        toast.error(`Trip ID "${tripId}" already exists.`);
        setErrors(p => ({ ...p, tripId: true }));
        setSaving(false);
        return;
      }
      const uploaded = await uploadAll(tripId.trim());

      // resolve job / rfq reference info (optional)
      let assignment = null;
      if (assignedId) {
        const collName = tripType === 'Delivery' ? 'Jobs' : 'RFQs';
        const snap = await getDoc(doc(db, collName, assignedId));
        if (snap.exists()) assignment = { id: snap.id, from: collName, ...snap.data() };
      }

      await addDoc(collection(db, 'Trips'), {
        tripId: tripId.trim(),
        tripType,
        tripDate,
        tripStatus,

        // costs
        tripCostTemplateId: tripCostTemplateId || null,
        tripCostBase: parseFloat(baseCost) || 0,
        sleepover,
        sleepoverExtra: sleepover ? OVERNIGHT_EXTRA : 0,
        tripCost: totalCost, // total saved

        // driver / vehicle (denorm a few helpful fields)
        driverRefId: driverSel?.id || null,
        driver: driverSel ? {
          name: driverSel.name || '',
          licenseNumber: driverSel.licenseNumber || '',
          phone: driverSel.phone || '',
          email: driverSel.email || ''
        } : null,

        vehicleRefId: vehicleSel?.id || null,
        vehicle: vehicleSel ? {
          name: vehicleSel.name || '',
          numberPlate: vehicleSel.numberPlate || '',
          make: vehicleSel.make || '',
          model: vehicleSel.model || '',
          year: vehicleSel.year || ''
        } : null,

        // assignment
        assignedId,
        assignment,

        // meta
        authId: user.uid,
        fileMeta: uploaded.fileMeta,
        fileUrls: uploaded.fileUrls,
        createdAt: serverTimestamp()
      });

      toast.success('Trip saved!');

      // quick reset
      setTripId(p => `TRIP${parseInt(p.replace(/\D/g, '') || '0', 10) + 1}`);
      setAssignedId('');
      setDriverSelId('');
      setVehicleSelId('');
      setTripCostTemplateId('');
      setBaseCost('');
      setSleepover(false);
      setFiles([]); setFileMeta([]);
    } catch (err) {
      console.error(err);
      toast.error('Save failed.');
    } finally { setSaving(false); }
  };

  /* ───────── render ───────── */
  return (
    <div className="collection-page">
      <ToastContainer position="top-right" autoClose={2500} />

      <Sidebar />

      <main className="collection-main">
        <div className="title-row">
          <h2>Collection / Delivery Trip</h2>
          <span className="badge-id">{tripId}</span>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <FaSpinner className="spin" size={54} />
            <p>Loading prerequisite data…</p>
          </div>
        ) : (
          <form className="collection-form" onSubmit={saveTrip}>
            {/* ───────── SECTION 1 – Trip Details ───────── */}
            <section className="card">
              <h3>Trip Details</h3>
              <div className="grid-4">
                <div className="field">
                  <label>Trip ID <span className="required">*</span></label>
                  <input
                    value={tripId}
                    onChange={e => setTripId(e.target.value)}
                    className={errors.tripId ? 'input-error' : ''}
                  />
                  {errors.tripId && <span className="error-text">Required & unique</span>}
                </div>

                <div className="field">
                  <label>Type</label>
                  <select value={tripType} onChange={e => setTripType(e.target.value)}>
                    {TRIP_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>

                <div className="field">
                  <label>Date</label>
                  <input
                    type="date"
                    value={tripDate}
                    onChange={e => setTripDate(e.target.value)}
                  />
                </div>

                <div className="field">
                  <label>Status</label>
                  <select value={tripStatus} onChange={e => setTripStatus(e.target.value)}>
                    {TRIP_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* ───────── SECTION 2 – Driver & Vehicle (dropdowns only) ───────── */}
            <section className="card">
              <h3>Driver & Vehicle</h3>
              <div className="grid-4">
                <div className="field">
                  <label>Driver <span className="required">*</span></label>
                  <select
                    value={driverSelId}
                    onChange={e => setDriverSelId(e.target.value)}
                    className={errors.driverSelId ? 'input-error' : ''}
                  >
                    <option value="">— Select Driver —</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.name} {d.licenseNumber ? `— ${d.licenseNumber}` : ''}
                      </option>
                    ))}
                  </select>
                  {errors.driverSelId && <span className="error-text">Required</span>}
                </div>

                <div className="field">
                  <label>Vehicle <span className="required">*</span></label>
                  <select
                    value={vehicleSelId}
                    onChange={e => setVehicleSelId(e.target.value)}
                    className={errors.vehicleSelId ? 'input-error' : ''}
                  >
                    <option value="">— Select Vehicle —</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.name} {v.numberPlate ? `— ${v.numberPlate}` : ''}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleSelId && <span className="error-text">Required</span>}
                </div>
              </div>
            </section>

            {/* ───────── SECTION 3 – Assignment & Costing ───────── */}
            <section className="card">
              <h3>Assignment & Costing</h3>
              <div className="grid-4">
                <div className="field">
                  <label>{tripType === 'Delivery' ? 'Job' : 'RFQ'} <span className="required">*</span></label>
                  <select
                    value={assignedId}
                    onChange={e => setAssignedId(e.target.value)}
                    className={errors.assignedId ? 'input-error' : ''}
                  >
                    <option value="">Select</option>
                    {tripType === 'Delivery'
                      ? jobs.map(j => (
                          <option key={j.id} value={j.id}>
                            {j.jobId} — {j.jobName || 'Untitled'}
                          </option>
                        ))
                      : rfqs.map(r => (
                          <option key={r.id} value={r.id}>
                            {r.rfqNo || r.rfqId || r.id}
                          </option>
                        ))}
                  </select>
                  {errors.assignedId && <span className="error-text">Required</span>}
                </div>

                <div className="field">
                  <label>Trip Cost (Template)</label>
                  <select value={tripCostTemplateId} onChange={onPickTemplate}>
                    <option value="">— Choose a cost —</option>
                    {tripCosts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.label} — {Number(c.amount || 0).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label>Base Amount (ZAR)</label>
                  <input
                    type="number"
                    min="0"
                    step=".01"
                    value={baseCost}
                    onChange={e => setBaseCost(e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="field chk-field">
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={sleepover}
                      onChange={e => onToggleSleepover(e.target.checked)}
                    />
                    <span>Overnight (+R{OVERNIGHT_EXTRA})</span>
                  </label>
                </div>

                <div className="field">
                  <label>Total Trip Cost</label>
                  <div className="total-chip">R{totalCost.toFixed(2)}</div>
                </div>
              </div>

              {/* Attachments */}
              <div className="upload-wrapper">
                <p className="hint">
                  Upload images / PDF / CSV (max {MAX_FILES} files, {MAX_SIZE_MB} MB each).
                </p>
                <div
                  className={`upload-area ${dragActive ? 'drag' : ''}`}
                  onClick={openPicker}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  role="button"
                  tabIndex={0}
                >
                  <FaCloudUploadAlt size={34} />
                  <p>{dragActive ? 'Drop to upload' : 'Click or drag files here'}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,text/csv,.csv"
                    multiple
                    hidden
                    onChange={handleInputFiles}
                  />
                </div>

                {fileMeta.length > 0 && (
                  <div className="files-grid">
                    {fileMeta.map((f, i) => (
                      <div key={i} className="file-chip">
                        <div className={`preview ${f.kind}`}>
                          {f.kind === 'image' ? (
                            <img src={f.preview} alt={f.name} />
                          ) : (
                            <span className="badge-kind">{f.kind.toUpperCase()}</span>
                          )}
                        </div>
                        <div className="fname" title={f.name}>{f.name}</div>
                        <button
                          type="button"
                          className="remove-chip"
                          onClick={() => removeFile(i)}
                          aria-label={`Remove ${f.name}`}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* ───────── Actions ───────── */}
            <section className="card">
              <div className="actions-row">
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Trip'}
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => navigate(0)}
                  disabled={saving}
                >
                  Clear Form
                </button>
              </div>
            </section>
          </form>
        )}
      </main>

      {/* overlay */}
      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={58} />
          <p>Saving…</p>
        </div>
      )}
    </div>
  );
}
